import React from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Download, Clock, HardDrive, User, FileVideo, Monitor, Camera, AlertCircle, RefreshCw, Trash2, Mail, Calendar, Info } from 'lucide-react';
import VideoStatusBadge from './VideoStatusBadge';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VideoCard = ({ video, onDownload, onRetry, onDelete }) => {
  const [retrying, setRetrying] = React.useState(false);
  const [downloading, setDownloading] = React.useState(false);
  const [downloadProgress, setDownloadProgress] = React.useState(0);
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  
  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  const formatDateYMD = (isoString) => {
    if (!isoString) return null;
    try {
      const date = new Date(isoString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    } catch {
      return null;
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return null;
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    if (mins >= 60) {
      const hrs = Math.floor(mins / 60);
      const remainMins = mins % 60;
      return `${hrs}س ${remainMins}د`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getExamName = () => {
    const name = video.exam_name || video.exam_title;
    if (name && name !== 'Unknown' && name !== 'unknown' && name !== 'Unknown Exam' && name !== 'Untitled') {
      return name;
    }
    return null; // Return null to hide if not available
  };

  const getVideoTypeIcon = (type) => {
    return type === 'screen' ? Monitor : Camera;
  };

  const VideoTypeIcon = getVideoTypeIcon(video.video_type);
  const examName = getExamName();

  const handleDownload = async () => {
    try {
      setDownloading(true);
      setDownloadProgress(0);
      
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${BACKEND_URL}/api/teacher/download-video/${video.recording_id}/${video.video_type}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to download video');
      }

      // Get content length for progress calculation
      const contentLength = response.headers.get('content-length');
      const total = parseInt(contentLength, 10);
      
      // Read response with progress tracking
      const reader = response.body.getReader();
      const chunks = [];
      let receivedLength = 0;

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        chunks.push(value);
        receivedLength += value.length;
        
        // Update progress
        if (total) {
          const progress = Math.round((receivedLength / total) * 100);
          setDownloadProgress(progress);
        }
      }

      // Combine chunks into blob
      const blob = new Blob(chunks);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${video.student_name}_${video.video_type}_${video.recording_id}.mp4`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('تم تحميل الفيديو بنجاح');
      
      if (onDownload) {
        onDownload(video);
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('فشل تحميل الفيديو');
    } finally {
      setDownloading(false);
      setDownloadProgress(0);
    }
  };

  const handleRetry = async () => {
    try {
      setRetrying(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(
        `${BACKEND_URL}/api/teacher/videos/${video.recording_id}/${video.video_type}/retry`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to retry');
      }

      toast.success('تم بدء إعادة المحاولة');
      
      if (onRetry) {
        onRetry(video);
      }
    } catch (error) {
      console.error('Retry error:', error);
      toast.error(error.message || 'فشلت إعادة المحاولة');
    } finally {
      setRetrying(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      const token = localStorage.getItem('token');
      
      const response = await fetch(
        `${BACKEND_URL}/api/teacher/video-files/${video.recording_id}?video_type=${video.video_type}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete');
      }

      toast.success('تم حذف الفيديو بنجاح');
      setShowDeleteDialog(false);
      
      if (onDelete) {
        onDelete(video);
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.message || 'فشل حذف الفيديو');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card className="hover:shadow-md transition-shadow bg-white">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          {/* Left: Icon + Info */}
          <div className="flex items-center gap-2.5 min-w-0 flex-1">
            <div className={`p-2 rounded-lg flex-shrink-0 ${
              video.video_type === 'screen' 
                ? 'bg-purple-50 text-purple-600' 
                : 'bg-blue-50 text-blue-600'
            }`}>
              <VideoTypeIcon className="w-5 h-5" />
            </div>
            <div className="min-w-0 flex-1">
              {examName && (
                <h3 className="font-semibold text-sm text-gray-900 truncate">
                  {examName}
                </h3>
              )}
              {/* Sender Email - Clear & Visible */}
              <p className="text-sm text-blue-600 font-medium" title={video.user_email || video.student_email}>
                {video.user_email || video.student_email || video.student_name}
              </p>
            </div>
          </div>
          
          {/* Right: Status + Delete */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <Button
              variant="ghost"
              size="icon"
              className="text-gray-400 hover:text-red-600 hover:bg-red-50 h-7 w-7"
              onClick={() => setShowDeleteDialog(true)}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0 space-y-3">
        {/* Info Row */}
        <div className="flex items-center justify-between text-sm py-2 px-3 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-4">
            <span className="text-gray-500 flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" />
              {formatDateYMD(video.submission_datetime || video.created_at) || '-'}
            </span>
            {formatDuration(video.duration_seconds) && (
              <span className="text-gray-500 flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                {formatDuration(video.duration_seconds)}
              </span>
            )}
          </div>
          <span className={`text-xs px-2 py-0.5 rounded font-medium ${
            video.video_type === 'screen' 
              ? 'bg-purple-100 text-purple-700' 
              : 'bg-blue-100 text-blue-700'
          }`}>
            {video.video_type === 'screen' ? 'شاشة' : 'كاميرا'}
          </span>
        </div>

        {/* Error State */}
        {video.status === 'file_missing' && (
          <div className="flex items-center gap-2 p-2 bg-orange-50 text-orange-700 rounded-lg text-xs">
            <AlertCircle className="w-4 h-4" />
            <span>الملف غير متوفر</span>
          </div>
        )}

        {/* Progress Bar */}
        {video.status !== 'ready' && video.status !== 'failed' && video.status !== 'file_missing' && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-gray-600">
              <span>التقدم</span>
              <span>{video.progress_percentage}%</span>
            </div>
            <Progress value={video.progress_percentage} className="h-2" />
          </div>
        )}

        {/* Error Message with Retry */}
        {video.status === 'failed' && (
          <div className="space-y-3">
            {video.error_message && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-800">
                  <p className="font-medium mb-1">فشلت العملية</p>
                  <p className="text-xs text-red-700">{video.error_message}</p>
                </div>
              </div>
            )}
            
            <Button
              onClick={handleRetry}
              disabled={retrying}
              className="w-full"
              variant="outline"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${retrying ? 'animate-spin' : ''}`} />
              {retrying ? 'جاري إعادة المحاولة...' : 'إعادة المحاولة'}
            </Button>
          </div>
        )}

        {/* Download Button - Clear & Visible */}
        {video.status === 'ready' && (
          <div className="space-y-2">
            {downloading && (
              <div className="space-y-1">
                <div className="flex justify-between text-xs text-gray-600">
                  <span>جاري التحميل...</span>
                  <span>{downloadProgress}%</span>
                </div>
                <Progress value={downloadProgress} className="h-2" />
              </div>
            )}
            
            <Button
              onClick={handleDownload}
              disabled={downloading}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3"
              size="lg"
            >
              <Download className={`w-5 h-5 ml-2 ${downloading ? 'animate-bounce' : ''}`} />
              {downloading ? `جاري التحميل ${downloadProgress}%` : 'تحميل الفيديو'}
            </Button>
          </div>
        )}

        {/* Disabled Download for non-ready */}
        {video.status !== 'ready' && video.status !== 'failed' && (
          <Button
            disabled
            className="w-full opacity-50 cursor-not-allowed"
            variant="outline"
          >
            <Download className="w-4 h-4 ml-2" />
            تحميل الفيديو (غير جاهز)
          </Button>
        )}

        {/* Metadata */}
        {video.status === 'ready' && video.compression_ratio && (
          <div className="text-xs text-gray-500 text-center pt-2 border-t">
            تم الضغط بنسبة {video.compression_ratio.toFixed(1)}%
          </div>
        )}
      </CardContent>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>تأكيد حذف الفيديو</AlertDialogTitle>
            <AlertDialogDescription>
              هل أنت متأكد من حذف هذا الفيديو؟
              <br />
              <strong>الطالب:</strong> {video.student_name || video.student_email}
              <br />
              <strong>الامتحان:</strong> {video.exam_name || video.exam_title}
              <br />
              <strong>النوع:</strong> {video.video_type === 'screen' ? 'تسجيل الشاشة' : 'تسجيل الكاميرا'}
              <br /><br />
              <span className="text-red-600">هذا الإجراء لا يمكن التراجع عنه.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>إلغاء</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? 'جاري الحذف...' : 'حذف الفيديو'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default VideoCard;
